<template>
  <!-- Breadcrumb -->
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" />
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <router-link :to="{ name: 'pages.admin.videos' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.videos", "Videos") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm tw-line-clamp-1">{{ video?.title || $t('common.view', 'View') }}</span>
    </button>
  </div>

  <div class="tw-p-2">
    <div v-if="loading" class="tw-py-16 tw-text-center tw-text-gray-400">
      <icon icon="eos-icons:three-dots-loading" class="tw-text-4xl" />
    </div>

    <div v-else-if="video" class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-6">
      <!-- Player + meta -->
      <div class="xl:tw-col-span-8 tw-space-y-4">
        <VideoPlayer :video="video" />
        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
          <div class="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <h1 class="tw-text-xl tw-font-bold tw-text-gray-900">{{ video.title }}</h1>
            <div class="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
              <a-button as="router-link" :to="{ name: 'pages.admin.videos.edit', params: { id: video.id } }"
                intent="light" left-icon="solar:pen-bold-duotone">
                {{ $t('common.edit', 'Edit') }}
              </a-button>
            </div>
          </div>
          <p class="tw-text-gray-600 tw-mt-3 tw-whitespace-pre-line">{{ video.description || $t('videos.no-description', 'No description.') }}</p>
          <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-4">
            <span v-for="c in video.categories" :key="c.id" class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-600 tw-text-xs">{{ c.name }}</span>
          </div>
        </div>
      </div>

      <!-- Side info -->
      <div class="xl:tw-col-span-4">
        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6 tw-space-y-4">
          <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">{{ $t('videos.details', 'Details') }}</h2>
          <div class="tw-space-y-3 tw-text-sm">
            <div class="tw-flex tw-justify-between"><span class="tw-text-gray-500">{{ $t('videos.status', 'Status') }}</span><span class="tw-font-medium" :class="video.status === 'active' ? 'tw-text-green-600' : 'tw-text-gray-500'">{{ video.status }}</span></div>
            <div class="tw-flex tw-justify-between"><span class="tw-text-gray-500">{{ $t('videos.source', 'Source') }}</span><span class="tw-font-medium">{{ video.source_type === 'upload' ? $t('videos.source-upload', 'Upload') : (video.provider || 'URL') }}</span></div>
            <div v-if="video.duration" class="tw-flex tw-justify-between"><span class="tw-text-gray-500">{{ $t('videos.duration', 'Duration') }}</span><span class="tw-font-medium">{{ video.duration }}</span></div>
            <div class="tw-flex tw-justify-between"><span class="tw-text-gray-500">{{ $t('videos.col-views', 'Views') }}</span><span class="tw-font-medium">{{ video.views_count ?? 0 }}</span></div>
            <div v-if="video.created_by" class="tw-flex tw-justify-between"><span class="tw-text-gray-500">{{ $t('videos.created-by', 'Created by') }}</span><span class="tw-font-medium">{{ video.created_by.name }}</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import VideoPlayer from '@/components/video/VideoPlayer.vue';
import videosApi from '@/api/videos';
import useAlert from '@/composables/useAlert';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const loading = ref(true);
const video = ref(null);

const fetchVideo = async () => {
    loading.value = true;
    try {
        const response = await videosApi.admin.get(route.params.id);
        video.value = response.data?.data || null;
    } catch (error) {
        console.error('Error loading video:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        router.push({ name: 'pages.admin.videos' });
    } finally {
        loading.value = false;
    }
};

onMounted(fetchVideo);
</script>
